import React, { useState, useEffect } from 'react';
import { moonApi, zodiacApi } from '../../lib/api';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const MoonWidget = () => {
  const [moonData, setMoonData] = useState(null);
  const [zodiacData, setZodiacData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { theme } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [moonRes, zodiacRes] = await Promise.all([
        moonApi.getCurrent(),
        zodiacApi.getCurrent(),
      ]);
      setMoonData(moonRes.data);
      setZodiacData(zodiacRes.data);
    } catch (error) {
      console.error('Error loading moon/zodiac data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic gradient based on theme (dark and subtle)
  const getGradient = () => {
    if (theme) {
      return `linear-gradient(135deg, ${theme.primary_dark} 0%, ${theme.primary} 50%, ${theme.primary_light} 100%)`;
    }
    // Default sky blue
    return 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 50%, #0ea5e9 100%)';
  };

  const getLoadingBg = () => {
    if (theme) {
      return `linear-gradient(135deg, ${theme.primary_dark} 0%, ${theme.primary} 100%)`;
    }
    return 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 100%)';
  };

  if (loading) {
    return (
      <div 
        data-testid="moon-widget-loading" 
        className="rounded-2xl p-6 h-48 flex items-center justify-center"
        style={{ background: getLoadingBg() }}
      >
        <Loader2 className="w-8 h-8 animate-spin text-white/80" />
      </div>
    );
  }

  return (
    <div 
      data-testid="moon-widget" 
      className="rounded-2xl p-5 relative overflow-hidden"
      style={{ background: getGradient() }}
    >
      {/* Subtle glow in corner */}
      <div 
        className="absolute top-2 right-4 w-12 h-12 rounded-full opacity-30" 
        style={{
          background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 40%, transparent 70%)',
        }} 
      />
      
      <div className="relative z-10">
        {/* Header - Clean */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-white/80">
            {new Date().toLocaleDateString('pt-BR', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long' 
            })}
          </span>
        </div>

        {/* Moon Phase with subtle zodiac sign on left */}
        <div className="flex items-center gap-3 mb-4">
          {/* Subtle zodiac on left */}
          <span 
            className="text-xl text-white/30" 
            data-testid="zodiac-symbol" 
            title={zodiacData?.name || ''}
          >
            {zodiacData?.symbol || '♈'}
          </span>
          
          {/* Moon emoji */}
          <div className="text-4xl" data-testid="moon-emoji" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.3))' }}>
            {moonData?.emoji || '🌙'}
          </div>
          
          {/* Moon info */}
          <div>
            <p className="text-lg font-semibold text-white/90" data-testid="moon-phase">
              {moonData?.phase_name || 'Carregando...'}
            </p>
            <p className="text-xs text-white/60">
              {moonData?.illumination || 0}% iluminada
            </p>
          </div>
        </div>

        {/* Business tip - Very subtle, like a whisper */}
        <p className="text-xs text-white/40 italic text-center" data-testid="business-tip">
          ✨ {moonData?.business_tip || ''}
        </p>
      </div>
    </div>
  );
};

export default MoonWidget;
